import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import WarningOutlinedIcon from "@material-ui/icons/WarningOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

const AlertContext = createContext(null);

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 14,
    minWidth: 320,
    maxWidth: 440,
    boxShadow: "0 12px 40px rgba(15, 45, 77, 0.18)",
    overflow: "hidden",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    paddingTop: theme.spacing(2.25),
    paddingBottom: theme.spacing(0.5),
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  title: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: "#0f2d4d",
    lineHeight: 1.25,
  },
  message: {
    color: "#374151",
    fontSize: "0.95rem",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  actions: {
    padding: theme.spacing(1, 2.5, 2.25),
    gap: theme.spacing(1),
  },
  okBtn: {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    minWidth: 88,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  cancelBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    minWidth: 88,
    color: "#4b5563",
  },
}));

const TYPE_META = {
  success: {
    title: "Success",
    color: "#1b7a3d",
    bg: "#eaf7ef",
    border: "#b7e0c4",
    Icon: CheckCircleOutlineIcon,
    btn: "#1b7a3d",
  },
  error: {
    title: "Error",
    color: "#b42318",
    bg: "#fef3f2",
    border: "#fecdca",
    Icon: ErrorOutlineIcon,
    btn: "#b42318",
  },
  warning: {
    title: "Warning",
    color: "#b54708",
    bg: "#fffaeb",
    border: "#fedf89",
    Icon: WarningOutlinedIcon,
    btn: "#b54708",
  },
  confirm: {
    title: "Confirm",
    color: "#b54708",
    bg: "#fffaeb",
    border: "#fedf89",
    Icon: WarningOutlinedIcon,
    btn: "#0f2d4d",
  },
  delete: {
    title: "Delete",
    color: "#b42318",
    bg: "#fef3f2",
    border: "#fecdca",
    Icon: DeleteOutlineIcon,
    btn: "#b42318",
  },
  info: {
    title: "Notice",
    color: "#175cd3",
    bg: "#eff8ff",
    border: "#b2ddff",
    Icon: InfoOutlinedIcon,
    btn: "#0f2d4d",
  },
};

const nativeAlert =
  typeof window !== "undefined" && typeof window.alert === "function"
    ? window.alert.bind(window)
    : (msg) => {
        // eslint-disable-next-line no-console
        console.log(String(msg ?? ""));
      };

const nativeConfirm =
  typeof window !== "undefined" && typeof window.confirm === "function"
    ? window.confirm.bind(window)
    : () => true;

function inferType(message) {
  const text = String(message || "").toLowerCase();
  if (
    /\b(error|failed|failure|unable|invalid|not allowed|forbidden|denied)\b/.test(
      text,
    )
  ) {
    return "error";
  }
  if (/\b(warning|caution|please select|required|missing)\b/.test(text)) {
    return "warning";
  }
  if (
    /\b(success|successfully|saved|updated|deleted|sent|created|uploaded)\b/.test(
      text,
    )
  ) {
    return "success";
  }
  return "info";
}

function inferConfirmType(message) {
  const text = String(message || "").toLowerCase();
  if (/\b(delete|remove|permanently)\b/.test(text)) return "delete";
  return "confirm";
}

let externalShowAlert = null;
let externalShowConfirm = null;

/** Show a styled popup from anywhere (same API as alert). */
export function showAppAlert(message, options = {}) {
  if (typeof externalShowAlert === "function") {
    return externalShowAlert(message, options);
  }
  nativeAlert(String(message ?? ""));
  return Promise.resolve();
}

/**
 * Show a styled confirm popup. Returns Promise<boolean>.
 * Prefer this over window.confirm (async-safe).
 */
export function showAppConfirm(message, options = {}) {
  if (typeof externalShowConfirm === "function") {
    return externalShowConfirm(message, options);
  }
  return Promise.resolve(nativeConfirm(String(message ?? "")));
}

export function AlertProvider({ children }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("alert"); // alert | confirm
  const [message, setMessage] = useState("");
  const [type, setType] = useState("info");
  const [title, setTitle] = useState("");
  const [confirmLabel, setConfirmLabel] = useState("OK");
  const [cancelLabel, setCancelLabel] = useState("Cancel");
  const resolveRef = useRef(null);
  const queueRef = useRef([]);
  const showingRef = useRef(false);

  const presentNext = useCallback(() => {
    if (showingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    showingRef.current = true;
    setMode(next.mode);
    setMessage(next.message);
    setType(next.type);
    setTitle(next.title || "");
    setConfirmLabel(
      next.confirmLabel || (next.mode === "confirm" ? "Confirm" : "OK"),
    );
    setCancelLabel(next.cancelLabel || "Cancel");
    resolveRef.current = next.resolve;
    setOpen(true);
  }, []);

  const enqueue = useCallback(
    (item) => {
      queueRef.current.push(item);
      presentNext();
    },
    [presentNext],
  );

  const showAlert = useCallback(
    (msg, options = {}) => {
      const text = msg == null ? "" : String(msg);
      const resolvedType = options.type || inferType(text);
      return new Promise((resolve) => {
        enqueue({
          mode: "alert",
          message: text,
          type: resolvedType,
          title: options.title || "",
          confirmLabel: options.okLabel || "OK",
          resolve: () => resolve(),
        });
      });
    },
    [enqueue],
  );

  const showConfirm = useCallback(
    (msg, options = {}) => {
      const text = msg == null ? "" : String(msg);
      const resolvedType = options.type || inferConfirmType(text);
      const defaultConfirm = resolvedType === "delete" ? "Delete" : "Confirm";
      return new Promise((resolve) => {
        enqueue({
          mode: "confirm",
          message: text,
          type: resolvedType,
          title: options.title || "",
          confirmLabel: options.confirmLabel || defaultConfirm,
          cancelLabel: options.cancelLabel || "Cancel",
          resolve,
        });
      });
    },
    [enqueue],
  );

  const finish = useCallback(
    (result) => {
      setOpen(false);
      showingRef.current = false;
      const resolve = resolveRef.current;
      resolveRef.current = null;
      if (typeof resolve === "function") resolve(result);
      setTimeout(() => presentNext(), 120);
    },
    [presentNext],
  );

  const handleClose = useCallback(() => {
    finish(mode === "confirm" ? false : undefined);
  }, [finish, mode]);

  const handleConfirm = useCallback(() => {
    finish(mode === "confirm" ? true : undefined);
  }, [finish, mode]);

  const handleCancel = useCallback(() => {
    finish(false);
  }, [finish]);

  useEffect(() => {
    externalShowAlert = showAlert;
    externalShowConfirm = showConfirm;

    window.alert = (msg) => {
      showAlert(msg);
    };

    window.confirm = (msg) => showConfirm(msg);

    return () => {
      window.alert = nativeAlert;
      window.confirm = nativeConfirm;
      if (externalShowAlert === showAlert) externalShowAlert = null;
      if (externalShowConfirm === showConfirm) externalShowConfirm = null;
    };
  }, [showAlert, showConfirm]);

  const meta = TYPE_META[type] || TYPE_META.info;
  const Icon = meta.Icon;
  const value = useMemo(
    () => ({ showAlert, showConfirm }),
    [showAlert, showConfirm],
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: classes.paper }}
        aria-labelledby="app-alert-title"
        aria-describedby="app-alert-message"
      >
        <DialogTitle disableTypography id="app-alert-title">
          <Box className={classes.titleRow}>
            <Box
              className={classes.iconWrap}
              style={{
                backgroundColor: meta.bg,
                border: `1px solid ${meta.border}`,
                color: meta.color,
              }}
            >
              <Icon fontSize="small" />
            </Box>
            <Typography className={classes.title}>
              {title || meta.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography id="app-alert-message" className={classes.message}>
            {message}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.actions}>
          {mode === "confirm" && (
            <Button className={classes.cancelBtn} onClick={handleCancel}>
              {cancelLabel}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.okBtn}
            onClick={handleConfirm}
            autoFocus
            style={{ backgroundColor: meta.btn }}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </AlertContext.Provider>
  );
}

export function useAppAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    return {
      showAlert: (message, options) => showAppAlert(message, options),
      showConfirm: (message, options) => showAppConfirm(message, options),
    };
  }
  return ctx;
}
